import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/franchisePay";
import { Benefits, HowItWorks, CommissionModel, Investment } from "./sections";
import { DashboardPreview, Faq } from "./sections2";

function useSeo() {
  useEffect(() => {
    const prev = document.title;
    document.title = "VIRIKS Institute Franchise | Partner with VIRIKS";
    const url = `${window.location.origin}/institute-franchise`;
    const tags = [
      ["name", "description", "Partner with VIRIKS to offer professional training and certification programs through your institute. Enroll students and track eligible commissions in a digital dashboard."],
      ["property", "og:title", "VIRIKS Institute Franchise"],
      ["property", "og:description", "Build your own training business with VIRIKS."],
      ["property", "og:image", `${window.location.origin}/franchise/hero.jpg`],
      ["property", "og:url", url], ["property", "og:type", "website"],
    ];
    const els = tags.map(([attr, key, content]) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      if (created) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      const old = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, created, old };
    });
    const link = document.createElement("link");
    link.rel = "canonical"; link.href = url; document.head.appendChild(link);
    return () => {
      document.title = prev; link.remove();
      els.forEach(({ el, created, old }) => (created ? el.remove() : el.setAttribute("content", old || "")));
    };
  }, []);
}

function Hero({ onApply }) {
  return (
    <section className="relative min-h-[88vh] flex items-center overflow-hidden" data-testid="fr-hero">
      <img src="/franchise/hero.jpg" alt="Trainer guiding students at a computer in a training institute" className="absolute inset-0 w-full h-full object-cover" fetchpriority="high" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/85 to-[#111111]/30" />
      <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
        <div className="max-w-2xl text-white fade-up">
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-white/80 border border-white/20 rounded-full px-4 py-1.5 mb-6">VIRIKS Institute Franchise</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]">Build Your Own Training Business with <span className="text-amber">VIRIKS</span></h1>
          <p className="mt-6 text-base md:text-lg text-white/75 leading-relaxed">Partner with VIRIKS to offer professional training and certification programs through your institute. Enroll students, manage your learners, and track your eligible commissions through a dedicated digital dashboard.</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <button onClick={() => onApply("hero")} className="rounded-full viriks-amber px-7 py-3.5 font-semibold inline-flex items-center gap-2 hover:opacity-90 transition-opacity" data-testid="fr-hero-apply">Become a Franchise Partner <ArrowRight size={16} /></button>
            <button onClick={() => { track("cta_click", { cta: "explore_benefits" }); document.getElementById("benefits")?.scrollIntoView({ behavior: "smooth" }); }} className="rounded-full border border-white/30 px-7 py-3.5 font-semibold hover:bg-white/10 transition-colors" data-testid="fr-hero-benefits">Explore Partner Benefits</button>
          </div>
          <p className="mt-8 flex items-start gap-2 text-sm text-white/60 max-w-xl" data-testid="fr-trust"><ShieldCheck size={18} className="text-amber shrink-0 mt-0.5" /> Students enroll digitally on the VIRIKS platform, and every eligible commission is recorded in your dashboard ledger with a full transaction history.</p>
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onApply }) {
  return (
    <section className="relative overflow-hidden" data-testid="fr-final-cta">
      <img src="/franchise/cta.jpg" alt="Graduates celebrating after completing their programme" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#ED1C24]/85" />
      <div className="relative max-w-4xl mx-auto px-6 py-24 text-center text-white">
        <h2 className="font-heading text-3xl md:text-5xl font-black">Ready to Grow with VIRIKS?</h2>
        <p className="mt-4 text-white/85 text-base md:text-lg">Take the next step toward offering professional training programs through your institute.</p>
        <button onClick={() => onApply("final")} className="mt-8 rounded-full bg-white text-[#ED1C24] px-8 py-3.5 font-semibold inline-flex items-center gap-2 hover:bg-white/90 transition-colors" data-testid="fr-final-apply">Start Your Franchise Application <ArrowRight size={16} /></button>
      </div>
    </section>
  );
}

export default function FranchiseLanding() {
  useSeo();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/franchise/config").then(({ data }) => setCfg(data)); track("page_view"); }, []);
  const onApply = (cta) => { track("cta_click", { cta }); navigate("/institute-franchise/apply"); };

  return (
    <div data-testid="franchise-landing">
      <Hero onApply={onApply} />
      <Benefits />
      <HowItWorks />
      <CommissionModel pct={cfg?.institute_percent ?? 30} />
      <Investment fees={cfg} onApply={onApply} />
      <DashboardPreview />
      <Faq fees={cfg} pct={cfg?.institute_percent ?? 30} />
      <FinalCta onApply={onApply} />
    </div>
  );
}
